import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Wallet, CreditCard, Landmark, Smartphone, Zap, Layers } from "lucide-react";
import SplitPaymentOverlay from "./SplitPaymentOverlay";
import "./PaymentMode.css";

// Shaped after the `receivingAccount` Mongoose schema. Pass the real
// documents from the API via the `paymentAccounts` prop — this default
// set is just for local/demo use and mirrors the same fields.
const DEFAULT_ACCOUNTS = [
  {
    _id: "acc_cash",
    name: "Cash Drawer",
    paymentType: "cash",
    color: "#16a34a",
    active: true,
  },
  {
    _id: "acc_slpos1",
    name: "StoreLense Terminal",
    paymentType: "storelensePOS",
    storeLenseTerminalID: "SLT-0092",
    color: "#0f2a47",
    active: true,
  },
  {
    _id: "acc_pos1",
    name: "Moniepoint POS 1",
    paymentType: "POS",
    provider: "Moniepoint",
    terminalId: "TID-88213",
    merchantId: "MID-4471",
    serialNumber: "SN-22015",
    isPartner: { hasIntgration: true, sellToken: "mp-213456", postype: 1 },
    color: "#1d6fd8",
    active: true,
  },
  {
    _id: "acc_pos2",
    name: "Moniepoint POS 2",
    paymentType: "POS",
    provider: "Moniepoint",
    terminalId: "TID-88214",
    merchantId: "MID-4471",
    serialNumber: "SN-22016",
    color: "#1d6fd8",
    active: true,
  },
  {
    _id: "acc_card1",
    name: "Card Terminal",
    paymentType: "card",
    provider: "Interswitch",
    terminalId: "CT-3391",
    color: "#ea580c",
    active: true,
  },
  {
    _id: "acc_bank1",
    name: "GTBank Main",
    paymentType: "bankTransfer",
    bankName: "GTBank",
    accountName: "Pragma Solutions Ltd",
    accountNumber: "0123456789",
    color: "#f97316",
    active: true,
  },
  {
    _id: "acc_bank2",
    name: "Access Bank",
    paymentType: "bankTransfer",
    bankName: "Access Bank",
    accountName: "Pragma Solutions Ltd",
    accountNumber: "0987654321",
    color: "#dc2626",
    active: true,
  },
  {
    _id: "acc_mm1",
    name: "Opay Merchant",
    paymentType: "mobilePay",
    provider: "Opay",
    walletNumber: "08012345678",
    merchantCode: "OPM-7723",
    color: "#0ea5e9",
    active: true,
  },
  {
    _id: "acc_mm2",
    name: "PalmPay",
    paymentType: "mobilePay",
    provider: "PalmPay",
    walletNumber: "08087654321",
    merchantCode: "PP-5521",
    color: "#7c3aed",
    active: true,
  },
];

// paymentType -> section label + icon, in display order.
const TYPE_META = {
  cash: { label: "Cash", icon: Wallet },
  storelensePOS: { label: "StoreLense POS", icon: Zap },
  POS: { label: "POS Terminals", icon: CreditCard },
  card: { label: "Card", icon: CreditCard },
  bankTransfer: { label: "Bank Transfer", icon: Landmark },
  mobilePay: { label: "Mobile Money", icon: Smartphone },
};
const TYPE_ORDER = ["cash", "storelensePOS", "POS", "card", "bankTransfer", "mobilePay"];

function formatNaira(value) {
  const n = Number(value) || 0;
  return `₦${n.toLocaleString("en-NG", { maximumFractionDigits: 2 })}`;
}

// True when this account is wired to a partner integration — selecting it
// triggers a real API call to the partner (e.g. Kuda, Moniepoint) rather
// than just recording a manual payment.
function isPartnerAccount(account) {
  return Boolean(account?.isPartner?.hasIntgration);
}

// Secondary line shown under an account's name — pulled straight from the
// account's own fields, whichever apply to its paymentType.
function accountSubtitle(account) {
  switch (account.paymentType) {
    case "bankTransfer":
      return [account.bankName, account.accountNumber].filter(Boolean).join(" · ");
    case "POS":
    case "storelensePOS":
      return [account.provider, account.terminalId || account.storeLenseTerminalID].filter(Boolean).join(" · ");
    case "card":
      return [account.provider, account.terminalId].filter(Boolean).join(" · ");
    case "mobilePay":
      return [account.provider, account.walletNumber].filter(Boolean).join(" · ");
    default:
      return null;
  }
}

/**
 * PaymentMode
 *
 * Single account-picker dropdown (defaults to Cash Drawer) sourced from
 * receivingAccount documents — paymentType is read straight off the
 * chosen account, so there's no separate "Payment Method" step. A "Split
 * payment" action opens an overlay to divide the total due across
 * multiple accounts.
 *
 * Props:
 * - totalDue: number — amount owed for this sale
 * - paymentAccounts: array — receivingAccount documents ({ _id, name, paymentType, ...}),
 *     defaults to DEFAULT_ACCOUNTS. Inactive accounts (active:false) are filtered out.
 * - onChange: (payload) => void — fires on mount and whenever the payment
 *     selection changes, so the parent can keep its own state in sync
 *     without waiting for user interaction. `payload.splits` is always
 *     present — a one-entry array for a single payment, or the full
 *     distribution for a split payment — so the parent can read the
 *     distribution the same way regardless of mode.
 *     Single payment payload:
 *       { isSplit:false, paymentType, accountId, accountName, accountColor, reference, amountPaid, change,
 *         splits:[{accountId, accountName, accountColor, paymentType, amount, reference}] }
 *     Split payment payload:
 *       { isSplit:true, splits:[{accountId, accountName, accountColor, paymentType, amount, reference}], paid, remaining, change }
 */
export default function PaymentMode({ totalDue = 0, paymentAccounts = DEFAULT_ACCOUNTS, onChange }) {
  console.log(paymentAccounts, "paymentAccounts");

  const activeAccounts = paymentAccounts.filter((a) => a.active !== false);

  const [selectedId, setSelectedId] = useState(activeAccounts.find((a) => a.paymentType === "cash")?._id || activeAccounts[0]?._id);
  const [reference, setReference] = useState("");
  const [amountReceived, setAmountReceived] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const [isSplitOpen, setIsSplitOpen] = useState(false);
  const [splitResult, setSplitResult] = useState(null);

  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const isFirstRender = useRef(true);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Builds a normalized single-payment payload — always includes a
  // one-entry `splits` array so the parent can read `payload.splits`
  // the same way whether this is a single or split payment.
  const buildSinglePayload = (account, amountPaid, ref) => {
    const finalReference = account?.paymentType === "cash" ? "" : ref;
    return {
      isSplit: false,
      paymentType: account?.paymentType,
      accountId: account?._id,
      accountName: account?.name,
      accountColor: account?.color,
      provider: account?.provider,
      isPartner: account?.isPartner || null,
      reference: finalReference,
      amountPaid,

      serialNumber: account?.terminalId,
      change: Math.max(0, amountPaid - totalDue),
      splits: [
        {
          accountId: account?._id,
          accountName: account?.name,
          accountColor: account?.color,
          provider: account?.provider,
          isPartner: account?.isPartner || null,
          paymentType: account?.paymentType,
          serialNumber: account?.terminalId,
          amount: amountPaid,
          reference: finalReference,
        },
      ],
    };
  };

  // Fire once on mount so the parent's state starts in sync with the
  // default selection, without needing the user to interact first.
  useEffect(() => {
    const defaultAccount = activeAccounts.find((a) => a.paymentType === "cash") || activeAccounts[0];
    onChangeRef.current?.(buildSinglePayload(defaultAccount, totalDue, ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the transaction total changes, any split (or partial cash entry)
  // no longer adds up correctly — reset back to a single default payment,
  // and let the parent know so its own state resets too.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const defaultAccount = activeAccounts.find((a) => a.paymentType === "cash") || activeAccounts[0];
    setSelectedId(defaultAccount?._id);
    setReference("");
    setAmountReceived("");
    setSplitResult(null);
    setIsOpen(false);
    setIsSplitOpen(false);
    onChangeRef.current?.(buildSinglePayload(defaultAccount, totalDue, ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalDue]);

  const selectedAccount = activeAccounts.find((a) => a._id === selectedId) || activeAccounts[0];

  const grouped = TYPE_ORDER.map((paymentType) => ({
    paymentType,
    ...TYPE_META[paymentType],
    accounts: activeAccounts.filter((a) => a.paymentType === paymentType),
  })).filter((g) => g.accounts.length > 0);

  useEffect(() => {
    if (!isOpen) return;
    const positionMenu = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMenuStyle({ top: rect.bottom + 6, left: rect.left, minWidth: rect.width });
    };
    positionMenu();
    window.addEventListener("scroll", positionMenu, true);
    window.addEventListener("resize", positionMenu);
    const handleClickAway = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickAway);
    return () => {
      window.removeEventListener("scroll", positionMenu, true);
      window.removeEventListener("resize", positionMenu);
      document.removeEventListener("mousedown", handleClickAway);
    };
  }, [isOpen]);

  const emit = (patch = {}) => {
    if (!onChange) return;
    const received =
      patch.amountPaid !== undefined ? patch.amountPaid : selectedAccount?.paymentType === "cash" && amountReceived !== "" ? Number(amountReceived) : totalDue;
    const currentRef = patch.reference !== undefined ? patch.reference : selectedAccount?.paymentType === "cash" ? "" : reference;
    onChange(buildSinglePayload(selectedAccount, received, currentRef));
  };

  const handleSelectAccount = (account) => {
    setSelectedId(account._id);
    setReference("");
    setAmountReceived("");
    setIsOpen(false);
    setSplitResult(null);
    onChange?.(buildSinglePayload(account, totalDue, ""));
  };

  const handleSplitConfirm = (result) => {
    setSplitResult(result);
    setIsSplitOpen(false);
    onChange?.({ isSplit: true, ...result });
  };

  const changeDue = selectedAccount?.paymentType === "cash" && amountReceived !== "" ? Math.max(0, Number(amountReceived) - totalDue) : 0;

  // Once a split payment has been confirmed, show a compact summary instead
  // of the single-account picker, with an "Edit" action to reopen the overlay.
  if (splitResult) {
    return (
      <div className='pm-root'>
        <div className='pm-label'>Payment</div>
        <div className='pm-split-summary'>
          <div className='pm-split-summary__header'>
            <Layers size={16} strokeWidth={2} />
            <span>Split across {splitResult.splits.length} accounts</span>
            <button type='button' className='pm-split-summary__edit' onClick={() => setIsSplitOpen(true)}>
              Edit
            </button>
          </div>
          <ul className='pm-split-summary__list'>
            {splitResult.splits.map((s) => (
              <li key={s.id || s.accountId}>
                <span>{s.accountName}</span>
                <span>{formatNaira(s.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
        {isSplitOpen &&
          createPortal(
            <SplitPaymentOverlay
              totalDue={totalDue}
              paymentAccounts={activeAccounts}
              initialSplits={splitResult.splits}
              onClose={() => setIsSplitOpen(false)}
              onConfirm={handleSplitConfirm}
            />,
            document.body,
          )}
      </div>
    );
  }

  return (
    <div className='pm-root'>
      <div className='pm-label'>Payment</div>

      <div className='pm-row'>
        <button type='button' ref={triggerRef} className='pm-dropdown-trigger' onClick={() => setIsOpen((v) => !v)}>
          {selectedAccount &&
            (() => {
              const Icon = TYPE_META[selectedAccount.paymentType]?.icon || Wallet;
              return (
                <span className='pm-dropdown-trigger__swatch' style={{ background: selectedAccount.color || "#0f2a47" }}>
                  <Icon size={13} strokeWidth={2.5} />
                </span>
              );
            })()}
          <span className='pm-dropdown-trigger__label'>{selectedAccount?.name || "Select account"}</span>
          {isPartnerAccount(selectedAccount) && (
            <span className='pm-partner-badge' title='Partner integration — payment is confirmed via API'>
              <Zap size={10} strokeWidth={3} />
              Partner
            </span>
          )}
          <ChevronDown size={16} strokeWidth={2} className='pm-dropdown-trigger__chevron' />
        </button>

        <button type='button' className='pm-split-btn' onClick={() => setIsSplitOpen(true)}>
          <Layers size={14} strokeWidth={2} />
          Split payment
        </button>
      </div>

      {/*       {selectedAccount?.paymentType !== "cash" && (
        <div className='pm-reference'>
          <label htmlFor='pm-reference-input'>Reference</label>
          <input
            id='pm-reference-input'
            type='text'
            placeholder='Transaction reference'
            value={reference}
            onChange={(e) => {
              setReference(e.target.value);
              emit({ reference: e.target.value });
            }}
          />
        </div>
      )} */}

      {/*    {selectedAccount?.paymentType === "cash" && (
        <div className='pm-cash-received'>
          <div className='pm-cash-received__field'>
            <label htmlFor='pm-amount-received'>Amount received</label>
            <input
              id='pm-amount-received'
              type='number'
              placeholder={String(totalDue)}
              value={amountReceived}
              onChange={(e) => {
                setAmountReceived(e.target.value);
                emit({ amountPaid: e.target.value === "" ? totalDue : Number(e.target.value) });
              }}
            />
          </div>

          {amountReceived !== "" && (
            <div className='pm-cash-received__change'>
              <span>Change</span>
              <span>{formatNaira(changeDue)}</span>
            </div>
          )}
        </div>
      )} */}

      {isOpen &&
        menuStyle &&
        createPortal(
          <div ref={menuRef} className='pm-menu' style={{ top: menuStyle.top, left: menuStyle.left, minWidth: menuStyle.minWidth }}>
            {grouped.map((section, idx) => (
              <div key={section.paymentType} className='pm-menu__section'>
                {idx > 0 && <div className='pm-menu__divider' />}
                <div className='pm-menu__group-label'>{section.label}</div>
                {section.accounts.map((account) => {
                  const Icon = section.icon;
                  const active = account._id === selectedId;
                  const subtitle = accountSubtitle(account);
                  return (
                    <button
                      key={account._id}
                      type='button'
                      className={`pm-menu__item${active ? " pm-menu__item--active" : ""}`}
                      onClick={() => handleSelectAccount(account)}
                    >
                      <span className='pm-menu__swatch' style={{ background: account.color || "#0f2a47" }}>
                        <Icon size={13} strokeWidth={2.5} />
                      </span>
                      <span className='pm-menu__item-text'>
                        <span className='pm-menu__item-name'>{account.name}</span>
                        {subtitle && <span className='pm-menu__item-subtitle'>{subtitle}</span>}
                      </span>
                      {isPartnerAccount(account) && (
                        <span className='pm-partner-badge' title='Partner integration — payment is confirmed via API'>
                          <Zap size={10} strokeWidth={3} />
                          Partner
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>,
          document.body,
        )}

      {isSplitOpen &&
        createPortal(
          <SplitPaymentOverlay
            totalDue={totalDue}
            paymentAccounts={activeAccounts}
            initialSplits={[
              {
                id: "split-1",
                accountId: selectedId,
                amount: totalDue,
                reference: "",
                serialNumber: selectedAccount?.serialNumber || selectedAccount?.terminalId,
              },
            ]}
            onClose={() => setIsSplitOpen(false)}
            onConfirm={handleSplitConfirm}
          />,
          document.body,
        )}
    </div>
  );
}
